import GetPrivateData from '../Classes/GetPrivateData.js';
import cssToRules from '../Functions/cssToRules.js';

const headMap = new WeakMap;
const instanceMap = new WeakMap;
export default class Element
{
	constructor(parentElement, elementName)
	{
		if(parentElement instanceof Element)
		{
			Object.defineProperty
			(
				this,
				'parentElement',
				{
					value: parentElement
				}
			);
			parentElement = parentElement.element;
		}

		const privateData = getPrivateData.for(this);

		const element = privateData.element = parentElement.ownerDocument.createElement(this.tagName);
		const className = this.className;
		if(className.toLowerCase() !== this.tagName.toLowerCase())
		{
			element.className = className;
		}
		if(elementName)
		{
			element.name = elementName;
		}
		parentElement.appendChild(element);

		const head = element.ownerDocument.head;
		const headData = headMap.get(head) ||
		{
			styleElement: element.ownerDocument.createElement('style'),
			classSet: new WeakSet(),
			headValues: {},
			allVariableRules: {},
		};
		if(!headMap.has(head))
		{
			headMap.set(head,headData);
			head.appendChild(headData.styleElement);
			headData.styleElement.sheet.insertRule(':root{}',0);
		}

		const instanceCount = instanceMap.get(this.constructor) || 0;
		Object.defineProperty(this,'instance',{value:instanceCount});
		instanceMap.set(this.constructor,instanceCount+1);

		const cssText = this.cssText;
		if(!cssText)
		{
			return;
		}

		if(cssText.then)
		{
			cssText.then
			(
				cssText =>
				{
					installCss.call(this, headData, cssText);
				}
			);
		}
		else
		{
			installCss.call(this, headData, cssText);
		}
	}

	get cssText()
	{
		return ''
	}

	get element()
	{
		return getPrivateData.for(this).element;
	}

	get tagName()
	{
		return null;
	}

	get className()
	{
		return this.constructor.name.replace(/^./,v => v.toLowerCase());
	}

	get value()
	{
		return this.element.textContent;
	}

	set value(value)
	{
		const fragment = document.createDocumentFragment();

		[].concat(value)
		.forEach
		(
			part =>
			{
				switch(typeof part)
				{
					case 'string':
					{
						const node = document.createElement('span');
						node.textContent = part;
						fragment.appendChild(node);
						return;
					}
					case 'object':
					{
						if(!part)
						{
							return;
						}
						if(part.img && typeof part.img === 'string')
						{
							const node = document.createElement('img');
							node.src = part.img;
							fragment.appendChild(node);
						}
						return;
					}
				}
			}
		);

		this.element.innerHTML = '';
		this.element.appendChild(fragment);
	}

	setCssPropertyValue(property,value)
	{
		const head = this.element.ownerDocument.head;
		const headData = headMap.get(head);
		headData.headValues[property] = value;
		(headData.allVariableRules[property] || []).forEach
		(
			ruleConfig =>
			{
				const [ruleIndex,elementValues,ruleParts] = ruleConfig;
				headData.styleElement.sheet.insertRule
				(
					ruleParts
					.map
					(
						v => (v.slice(0,2) === '--')
							? (elementValues[v.slice(2)] || v)
							: v
					)
					.join(''),
					ruleIndex+1
				);
				headData.styleElement.sheet.deleteRule(ruleIndex);
			}
		)
	}

	dispatchEvent(type, options)
	{
		if(!options)
		{
			options = {};
		}

		const target = options.target || this.element;

		if(options.bubbles === undefined)
		{
			options.bubbles = true;
		}
		options.detail = options.detail || {};
		options.detail.instance = this.instance;

		target.dispatchEvent(new CustomEvent(type,options));
	}
}

const getPrivateData = new GetPrivateData(Element);

function installCss(headData,cssText)
{
	const {styleElement,classSet} = headData;

	// check to see if the classmap has the current class and if it does already, then nothing to do
	if(classSet.has(this.constructor))
	{
		return;
	}

	// we only want to add once so mark as added
	classSet.add(this.constructor);

	// begin adding the css rules
	const {rules,values} = cssToRules(cssText);

	const sheet = styleElement.sheet;

	const elementValues = Object.create(headData.headValues);

	rules.forEach
	(
		rule =>
		{
			if(rule.ruleParts[0].slice(0,5) === ':root')
			{
				rule.ruleParts[0].match(/--([^:]+:[^;}]+)/g)
				.map
				(
					v => v.slice(2).split(':')
				)
				.map
				(
					v => [v.shift(),v.join(':').trim()]
				)
				.forEach
				(
					v =>
					{
						if (v[0] in headData.headValues)
						{
							// console.error('Duplicate :root value detected,ignoring\n', v);
							// if value has been set programatically before
							// the css value is loaded then this will trigger,
							// which is valid, so don't show an error.
							return;
						}
						headData.headValues[v[0]] = v[1];
					}
				);

				Object.keys(values).forEach(key =>
				{
					if (!(key in headData.headValues))
					{
						elementValues[key] = values[key];
					}
				});

				return;
			}

			let ruleText = rule.ruleParts.map
			(
				rulePart =>
				{
					let value = null;
					if(rulePart.slice(0,2) === '--')
					{
						value = elementValues[rulePart.slice(2)];
					}
					return value || rulePart;
				}
			)
			.join('')
			.replace
			(
				/--[^:]+:[^;}]*;?/g,
				variable =>
				{
					variable = variable.slice(2).split(':').map(v => v.trim());
					elementValues[variable[0]] = variable[1].replace(';','');
					return '';
				}
			);
			try
			{
				const ruleIndex = sheet.insertRule(ruleText, sheet.cssRules.length);
				rule.cssVariables.forEach
				(
					cssVariable =>
					{
						headData.allVariableRules[cssVariable] = headData.allVariableRules[cssVariable] || [];
						headData.allVariableRules[cssVariable].push([ruleIndex, elementValues, rule.ruleParts]);
					}
				);
			}
			catch(e){}
		}
	);
}